import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
    ApproveRequestBatchRequest, ApproveRequestRequest, GetPendingRequestsResponse,
    GetTypedRequestsResponse, initialRequestState, PendingCountRow, RejectRequestBatchRequest,
    RejectRequestRequest, RequestDetailsResponse, RequestSliceModel
} from "./request-interfaces";
import { getSlugForRequestTypeId } from "../request-types-config";
import { ErrorResponse, MainResponse, RejectedValue } from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import { showNotification } from "shared/slices/statusNotificationsSlice";
import { NotificationTypes } from "shared/interfaces/status-notifications-interfaces";
import { API_BASE_URL as baseUri } from "config/env";

// GET REQUESTS BY TYPE: returns pending / my / all in one response. Not kept in the slice - the
// module screen that asked for them holds them (RequestTypeView).
export const GetRequests = createAsyncThunk<GetTypedRequestsResponse, { requestTypeId: number, includeAll: boolean }, { rejectValue: RejectedValue }>(
    'Slice/RequestsSlice/GetRequests',
    async (request, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/requests/get?requestTypeId=' + request.requestTypeId + '&includeAll=' + request.includeAll, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetTypedRequestsResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// GET PENDING COUNTS: aggregate pending requests across all types in one call.
// Only the request type ids are used, to badge each module in the sidebar.
export const GetPendingCounts = createAsyncThunk<PendingCountRow[], void, { rejectValue: RejectedValue }>(
    'Slice/RequestsSlice/GetPendingCounts',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/requests/pending', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetPendingRequestsResponse;
            return responseModel.requests;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// The approve/reject calls refresh the shared pending counts; the screen reloads its own list.

// REJECT REQUEST
export const RejectRequest = createAsyncThunk<MainResponse, RejectRequestRequest, { rejectValue: RejectedValue }>(
    'Slice/RequestsSlice/RejectRequest',
    async (payload, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/requests/reject', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify({ requestId: payload.requestId, description: payload.description })
        });
        if (response.status === 200) {
            dispatch(GetPendingCounts());
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: responseModel.message ?? "Request rejected successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// REJECT REQUEST BATCH
export const RejectRequestBatch = createAsyncThunk<MainResponse, RejectRequestBatchRequest, { rejectValue: RejectedValue }>(
    'Slice/RequestsSlice/RejectRequestBatch',
    async (payload, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/requests/reject/batch', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify({ requestIds: payload.requestIds, description: payload.description })
        });
        if (response.status === 200) {
            dispatch(GetPendingCounts());
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: responseModel.message ?? "Requests rejected successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// APPROVE REQUEST
export const ApproveRequest = createAsyncThunk<MainResponse, ApproveRequestRequest, { rejectValue: RejectedValue }>(
    'Slice/RequestsSlice/ApproveRequest',
    async (payload, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/requests/approve', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify({ requestId: payload.requestId, description: payload.description })
        });
        if (response.status === 200) {
            dispatch(GetPendingCounts());
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: responseModel.message ?? "Request approved successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// APPROVE REQUEST BATCH
export const ApproveRequestBatch = createAsyncThunk<MainResponse, ApproveRequestBatchRequest, { rejectValue: RejectedValue }>(
    'Slice/RequestsSlice/ApproveRequestBatch',
    async (payload, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/requests/approve/batch', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify({ requestIds: payload.requestIds, description: payload.description })
        });
        if (response.status === 200) {
            dispatch(GetPendingCounts());
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: responseModel.message ?? "Requests approved successfully.", type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// GET REQUEST DETAILS (additional-info — used by the detail panel for attachments).
export const GetRequestDetails = createAsyncThunk<RequestDetailsResponse, string, { rejectValue: RejectedValue }>(
    'Slice/RequestsSlice/GetRequestDetails',
    async (id, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/requests/additional-info?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as RequestDetailsResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const RequestsSlice = createSlice({
    name: "RequestSlice",
    initialState: initialRequestState,
    reducers: {},
    extraReducers(builder) {
        builder.addCase(GetPendingCounts.fulfilled, (state, action: PayloadAction<PendingCountRow[]>) => {
            state.pendingCountsBySlug = action.payload.reduce<Record<string, number>>((acc, row) => {
                const slug = getSlugForRequestTypeId(row.requestTypeId);
                acc[slug] = (acc[slug] ?? 0) + 1;
                return acc;
            }, {});
        });
        // A failed call counts as "nothing pending", so a module never waits on it for good.
        builder.addCase(GetPendingCounts.rejected, (state) => {
            state.pendingCountsBySlug = {};
        });
    }
});

export default RequestsSlice.reducer;
